"use client";

import {
  check_auth,
  createBlog,
  createCourse,
  deleteBlog,
  deleteCourse,
  getBlogById,
  getBlogCategories,
  getBlogs,
  getCoursesAdmin,
  getStudentDetail,
  logoutStudent,
  updateBlog,
  updateCourse,
  getEnquiries,
  deleteEnquiry,
  markEnquiryRead,
  markAllEnquiriesRead,
  getStudentsList,
  updateStudent,
  deleteStudent,
  enrollStudentInCourse,
  removeStudentFromCourse,
  getStudentProgress,
  uploadCourseFile,
} from "@/api";
import CommentEditor from "@/app/components/CommentBox/CommentEditor";
import { isAdminOrFacultyUser } from "@/app/lib/isAdminUser";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import "./admin.css";
import {
  Books,
  Users,
  EnvelopeOpen,
  Article,
  ChartPie,
  Trash,
  Pencil,
  Plus,
  CheckCircle,
  Clock,
  ArrowSquareOut,
  Folder,
  FilePdf,
  Video,
  CaretRight,
  CaretDown,
  X,
  SignOut,
  User,
  PlusCircle,
  Eye,
  ArrowClockwise,
  UserCirclePlus,
  Sun,
  Moon,
  BookOpen,
  List
} from "@phosphor-icons/react";


const initialCourseForm = {
  title: "",
  description: "",
  price: "",
  short_description: "",
  essential_features: "",
  is_discounted: false,
  discount_price: "",
  starting_date: "",
  batch_type: "",
  batch_days: "",
  batch_time: "",
  few_seats_left: false,
  display: false,
  cover_image: null,
  cover_image_url: "",
  subtitles: []
};

const initialBlogForm = {
  title: "",
  short_description: "",
  category: [],
  sub_category: [],
  author: "",
  cover_image: null,
  cover_image_url: "",
  content: "",
};

const initialStudentForm = {
  name: "",
  email: "",
  phone_number: "",
  role: "student"
};

const getEntityId = (item) => item?.id || item?._id || item?.course_id || "";

const normalizeCollection = (payload, keys) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  for (const key of keys) {
    if (Array.isArray(payload[key])) return payload[key];
  }

  if (Array.isArray(payload.data)) return payload.data;
  return [];
};

const firstNonEmptyString = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value;
  }
  return "";
};

const normalizeCategoryValue = (value) => String(value || "").trim();
const parseArrayLikeString = (value) => {
  if (typeof value !== "string") return null;
  let current = value.trim();
  for (let i = 0; i < 2; i += 1) {
    if (!(current.startsWith("[") && current.endsWith("]"))) break;
    try {
      const parsed = JSON.parse(current);
      if (Array.isArray(parsed)) return parsed;
      if (typeof parsed === "string") {
        current = parsed.trim();
        continue;
      }
    } catch {
      break;
    }
  }
  return null;
};

const toStringArray = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => normalizeCategoryValue(item))
      .filter(Boolean);
  }
  const parsed = parseArrayLikeString(value);
  if (parsed) {
    return parsed
      .map((item) => normalizeCategoryValue(item))
      .filter(Boolean);
  }
  const normalized = normalizeCategoryValue(value);
  return normalized ? [normalized] : [];
};

const dedupeByLowerCase = (values = []) => {
  const map = new Map();
  values.forEach((value) => {
    const normalized = normalizeCategoryValue(value);
    if (!normalized) return;
    const key = normalized.toLowerCase();
    if (!map.has(key)) map.set(key, normalized);
  });
  return Array.from(map.values());
};

const addUniqueValue = (values = [], value) =>
  dedupeByLowerCase([...values, value]);
const removeValue = (values = [], valueToRemove) => {
  const target = normalizeCategoryValue(valueToRemove).toLowerCase();
  return values.filter(
    (value) => normalizeCategoryValue(value).toLowerCase() !== target
  );
};

const getCategoryLabel = (item) => {
  if (typeof item === "string") return normalizeCategoryValue(item);
  if (!item || typeof item !== "object") return "";
  return normalizeCategoryValue(
    firstNonEmptyString(
      item.category,
      item.name,
      item.title,
      item.value,
      item.label,
      item.slug
    )
  );
};

const normalizeBlogCategories = (payload) => {
  const list = Array.isArray(payload)
    ? payload
    : payload?.categories ||
      payload?.items ||
      payload?.data ||
      payload?.result ||
      payload?.category ||
      [];

  if (!Array.isArray(list)) return [];

  const deduped = new Map();
  list.forEach((item) => {
    const label = getCategoryLabel(item);
    if (!label) return;
    const key = label.toLowerCase();
    if (!deduped.has(key)) deduped.set(key, label);
  });

  return Array.from(deduped.values());
};

const normalizeBlogTaxonomy = (payload) => {
  const categories = normalizeBlogCategories(payload);
  const rawSubCategories =
    payload?.sub_categories ||
    payload?.subCategories ||
    payload?.sub_category ||
    payload?.data?.sub_categories ||
    payload?.data?.subCategories ||
    [];
  const subCategories = dedupeByLowerCase(toStringArray(rawSubCategories));
  return { categories, subCategories };
};

const formatDateTimeLocalValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (part) => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const normalizeStringList = (value) =>
  dedupeByLowerCase(
    String(value || "")
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
  );

const mapBlogToForm = (blog) => {
  const existingCover = firstNonEmptyString(
    blog?.cover_image,
    blog?.coverImage,
    blog?.image,
    blog?.image_url
  );

  return {
    title: firstNonEmptyString(blog?.title, blog?.name),
    short_description: firstNonEmptyString(
      blog?.short_description,
      blog?.shortDescription,
      blog?.short_desc,
      blog?.excerpt,
      blog?.description,
      blog?.summary
    ),
    category: dedupeByLowerCase(toStringArray(blog?.category)),
    sub_category: dedupeByLowerCase(
      toStringArray(blog?.sub_category || blog?.subCategory)
    ),
    author: firstNonEmptyString(blog?.author, blog?.author_name),
    cover_image: null,
    cover_image_url: existingCover,
    content: firstNonEmptyString(
      blog?.content,
      blog?.blog_content,
      blog?.body,
      blog?.description_html,
      blog?.html_content,
      blog?.description
    ),
  };
};

export default function AdminDashboardPage() {
  const router = useRouter();
  const [activeTab, setActiveTab] = useState("overview");
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [darkMode, setDarkMode] = useState(true);
  const [courseCatalogTab, setCourseCatalogTab] = useState("active");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Core Entity States
  const [courses, setCourses] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [students, setStudents] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [blogCategories, setBlogCategories] = useState([]);
  const [blogSubCategories, setBlogSubCategories] = useState([]);

  // Form States
  const [courseForm, setCourseForm] = useState(initialCourseForm);
  const [blogForm, setBlogForm] = useState(initialBlogForm);
  const [studentForm, setStudentForm] = useState(initialStudentForm);

  // Edit Tracking
  const [editingCourseId, setEditingCourseId] = useState("");
  const [editingBlogId, setEditingBlogId] = useState("");
  const [editingStudentId, setEditingStudentId] = useState("");

  // Status Reports
  const [courseStatus, setCourseStatus] = useState({ type: "", message: "" });
  const [blogStatus, setBlogStatus] = useState({ type: "", message: "" });
  const [studentStatus, setStudentStatus] = useState({ type: "", message: "" });
  const [enquiryStatus, setEnquiryStatus] = useState({ type: "", message: "" });

  // Loaders
  const [isSubmittingCourse, setIsSubmittingCourse] = useState(false);
  const [isSubmittingBlog, setIsSubmittingBlog] = useState(false);
  const [isSubmittingStudent, setIsSubmittingStudent] = useState(false);

  // Blog Categories Helpers
  const [blogImagePreviewError, setBlogImagePreviewError] = useState(false);
  const [categoryDraft, setCategoryDraft] = useState("");
  const [subCategoryDraft, setSubCategoryDraft] = useState("");
  const [isAddingNewCategory, setIsAddingNewCategory] = useState(false);
  const [isAddingNewSubCategory, setIsAddingNewSubCategory] = useState(false);

  // Syllabus Editor State
  const [courseSyllabus, setCourseSyllabus] = useState([]); // Array of Subtitle sections
  const [editingSectionIndex, setEditingSectionIndex] = useState(null);
  const [sectionTitleDraft, setSectionTitleDraft] = useState("");

  // PDF Upload Panel State — one panel open per section at most
  const initialPdfPanel = {
    openIndex: null,    // which section index has the panel open
    title: "",          // document title typed by admin
    url: "",            // final CloudFront URL (set on upload success or manual paste)
    file: null,         // File object selected for upload
    uploading: false,   // true while S3 upload is in-flight
    uploadError: "",    // non-empty string if last upload failed
    uploadSuccess: false, // true after a successful upload
    uploadMode: "file", // 'file' | 'link'
  };
  const [pdfPanel, setPdfPanel] = useState(initialPdfPanel);

  // Student Details / Enrollment State
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentCoursesProgress, setStudentCoursesProgress] = useState({}); // courseId -> Progress Object
  const [progressLoadingMap, setProgressLoadingMap] = useState({});
  const [enrollCourseId, setEnrollCourseId] = useState("");

  // Search Filter
  const [studentSearch, setStudentSearch] = useState("");

  const editorInitialValue = useMemo(
    () => blogForm.content || "",
    [blogForm.content]
  );

  const refreshData = async () => {
    setIsLoadingData(true);
    try {
      const [
        courseResult,
        blogResult,
        categoryResult,
        studentsResult,
        enquiriesResult
      ] = await Promise.allSettled([
        getCoursesAdmin(),
        getBlogs(),
        getBlogCategories(),
        getStudentsList(),
        getEnquiries()
      ]);

      if (courseResult.status === "fulfilled") {
        setCourses(normalizeCollection(courseResult.value, ["courses", "course", "items"]));
      }
      if (blogResult.status === "fulfilled") {
        setBlogs(normalizeCollection(blogResult.value, ["blogs", "blog", "items"]));
      }
      if (categoryResult.status === "fulfilled") {
        const taxonomy = normalizeBlogTaxonomy(categoryResult.value);
        setBlogCategories(taxonomy.categories);
        setBlogSubCategories(taxonomy.subCategories);
      }
      if (studentsResult.status === "fulfilled") {
        setStudents(Array.isArray(studentsResult.value) ? studentsResult.value : []);
      }
      if (enquiriesResult.status === "fulfilled") {
        setEnquiries(Array.isArray(enquiriesResult.value) ? enquiriesResult.value : []);
      }
    } catch (error) {
      console.error("Error loading admin dashboard datasets:", error);
    } finally {
      setIsLoadingData(false);
    }
  };

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const session = await check_auth();
        if (!session) {
          router.replace("/admin/login");
          return;
        }

        const user = await getStudentDetail();
        if (!isAdminOrFacultyUser(user)) {
          router.replace("/admin/login");
          return;
        }
      } catch (error) {
        router.replace("/admin/login");
        return;
      } finally {
        setIsCheckingAccess(false);
      }

      await refreshData();
    };

    checkAccess();
  }, [router]);

  const handleLogout = async () => {
    try {
      await logoutStudent();
    } catch (error) {
      // no-op
    } finally {
      router.replace("/admin/login");
    }
  };

  // ----------------------------------------------------
  // Courses Operations & Form handlers
  // ----------------------------------------------------
  const handleCourseChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCourseForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCourseFile = (e) => {
    const file = e.target.files?.[0] || null;
    setCourseForm((prev) => ({
      ...prev,
      cover_image: file,
      cover_image_url: file ? URL.createObjectURL(file) : prev.cover_image_url,
    }));
  };

  const submitCourse = async (e) => {
    e.preventDefault();
    setCourseStatus({ type: "", message: "" });
    setIsSubmittingCourse(true);

    if (!editingCourseId && !courseForm.cover_image) {
      setCourseStatus({
        type: "error",
        message: "Course image is required for new course creation.",
      });
      setIsSubmittingCourse(false);
      return;
    }

    try {
      if (courseForm.is_discounted && !String(courseForm.discount_price).trim()) {
        setCourseStatus({
          type: "error",
          message: "Discount price is required when the course is discounted.",
        });
        setIsSubmittingCourse(false);
        return;
      }

      const coursePayload = {
        ...courseForm,
        subtitles: courseSyllabus, // Bind dynamic local syllabus editor array
        batch_days: normalizeStringList(courseForm.batch_days),
        discount_price: courseForm.is_discounted
          ? String(courseForm.discount_price || "").trim()
          : "",
      };

      if (editingCourseId) {
        await updateCourse(editingCourseId, coursePayload);
        setCourseStatus({ type: "success", message: "Course updated successfully!" });
      } else {
        await createCourse(coursePayload);
        setCourseStatus({ type: "success", message: "Course created successfully!" });
      }
      resetCourseForm();
      await refreshData();
    } catch (error) {
      setCourseStatus({
        type: "error",
        message:
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Course operation failed.",
      });
    } finally {
      setIsSubmittingCourse(false);
    }
  };

  const onEditCourse = (course) => {
    setEditingCourseId(getEntityId(course));
    setCourseForm({
      title: course?.title || "",
      description: course?.description || "",
      price: course?.price || "",
      short_description: course?.short_description || "",
      essential_features: course?.essential_features || "",
      is_discounted: Boolean(course?.is_discounted),
      discount_price: course?.discount_price || "",
      starting_date: formatDateTimeLocalValue(course?.starting_date),
      batch_type: course?.batch_type || "",
      batch_days: toStringArray(course?.batch_days).join(", "),
      batch_time: course?.batch_time || "",
      few_seats_left: Boolean(course?.few_seats_left),
      display: Boolean(course?.display),
      cover_image: null,
      cover_image_url: firstNonEmptyString(course?.cover_image),
    });
    setCourseSyllabus(Array.isArray(course?.subtitles) ? JSON.parse(JSON.stringify(course.subtitles)) : []);
  };

  const onDeleteCourse = async (id) => {
    if (!id) return;
    if (!window.confirm("Are you absolutely sure you want to delete this course?")) return;
    try {
      await deleteCourse(id);
      await refreshData();
      setCourseStatus({ type: "success", message: "Course deleted." });
    } catch (error) {
      setCourseStatus({
        type: "error",
        message: error?.response?.data?.message || "Course delete failed.",
      });
    }
  };

  const resetCourseForm = () => {
    setCourseForm(initialCourseForm);
    setEditingCourseId("");
    setCourseSyllabus([]);
    setEditingSectionIndex(null);
    setPdfPanel(initialPdfPanel); // close any open PDF upload panel
  };

  // ----------------------------------------------------
  // Syllabus Builder Helper Sub-methods
  // ----------------------------------------------------
  const addSyllabusSection = () => {
    if (!sectionTitleDraft.trim()) return;
    setCourseSyllabus((prev) => [
      ...prev,
      { title: sectionTitleDraft.trim(), videos: [], documents: [] }
    ]);
    setSectionTitleDraft("");
  };

  const removeSyllabusSection = (secIndex) => {
    setCourseSyllabus((prev) => prev.filter((_, i) => i !== secIndex));
    if (editingSectionIndex === secIndex) setEditingSectionIndex(null);
  };

  const addVideoToSection = (secIndex) => {
    const title = window.prompt("Enter Video Title:");
    if (!title) return;
    const path = window.prompt("Enter local-transcoded HLS master.m3u8 URL / Path:");
    if (!path) return;

    setCourseSyllabus((prev) => {
      const copy = [...prev];
      copy[secIndex].videos = [
        ...(copy[secIndex].videos || []),
        { title, path, comments: [] }
      ];
      return copy;
    });
  };

  // Open/close the inline PDF panel for a given section
  const openPdfPanel = (secIndex) => {
    setPdfPanel((prev) =>
      prev.openIndex === secIndex
        ? initialPdfPanel // toggle off if already open on same section
        : { ...initialPdfPanel, openIndex: secIndex }
    );
  };

  const closePdfPanel = () => setPdfPanel(initialPdfPanel);

  // Upload the selected file to S3 and store the returned URL
  const handlePdfUpload = async () => {
    if (!pdfPanel.file) return;
    setPdfPanel((prev) => ({ ...prev, uploading: true, uploadError: "", uploadSuccess: false, url: "" }));
    try {
      const result = await uploadCourseFile(pdfPanel.file);
      if (!result?.file_url) throw new Error("No file URL returned from server.");
      setPdfPanel((prev) => ({
        ...prev,
        uploading: false,
        uploadSuccess: true,
        url: result.file_url,
        uploadError: "",
      }));
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || "Upload failed. Please try again.";
      setPdfPanel((prev) => ({ ...prev, uploading: false, uploadError: msg, uploadSuccess: false }));
    }
  };

  // Confirm and add document to the syllabus section
  const handleAddDocument = (secIndex) => {
    const trimTitle = pdfPanel.title.trim();
    const trimUrl   = pdfPanel.url.trim();
    if (!trimTitle || !trimUrl) return;

    setCourseSyllabus((prev) => {
      const copy = JSON.parse(JSON.stringify(prev)); // deep clone to avoid mutation
      copy[secIndex].documents = [
        ...(copy[secIndex].documents || []),
        { title: trimTitle, path: trimUrl },
      ];
      return copy;
    });
    closePdfPanel();
  };

  const removeVideoFromSection = (secIndex, vIndex) => {
    setCourseSyllabus((prev) => {
      const copy = [...prev];
      copy[secIndex].videos = copy[secIndex].videos.filter((_, i) => i !== vIndex);
      return copy;
    });
  };

  const removeDocumentFromSection = (secIndex, dIndex) => {
    setCourseSyllabus((prev) => {
      const copy = [...prev];
      copy[secIndex].documents = copy[secIndex].documents.filter((_, i) => i !== dIndex);
      return copy;
    });
  };


  // ----------------------------------------------------
  // Students Operations & Form handlers
  // ----------------------------------------------------
  const handleStudentChange = (e) => {
    const { name, value } = e.target;
    setStudentForm((prev) => ({ ...prev, [name]: value }));
  };

  const submitStudent = async (e) => {
    e.preventDefault();
    setStudentStatus({ type: "", message: "" });
    setIsSubmittingStudent(true);

    try {
      if (editingStudentId) {
        await updateStudent(editingStudentId, studentForm);
        setStudentStatus({ type: "success", message: "Student updated successfully!" });
        if (selectedStudent && selectedStudent._id === editingStudentId) {
          setSelectedStudent((prev) => ({ ...prev, ...studentForm }));
        }
      }
      resetStudentForm();
      await refreshData();
    } catch (error) {
      setStudentStatus({
        type: "error",
        message: error?.response?.data?.error || "Student save failed."
      });
    } finally {
      setIsSubmittingStudent(false);
    }
  };

  const onEditStudent = (student) => {
    setEditingStudentId(student._id);
    setStudentForm({
      name: student.name || "",
      email: student.email || "",
      phone_number: student.phone_number || "",
      role: student.role || "student"
    });
  };

  const onDeleteStudent = async (studentId) => {
    if (!studentId) return;
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await deleteStudent(studentId);
      if (selectedStudent?._id === studentId) setSelectedStudent(null);
      await refreshData();
      setStudentStatus({ type: "success", message: "Student deleted successfully." });
    } catch (error) {
      setStudentStatus({ type: "error", message: "Failed to delete student." });
    }
  };

  const resetStudentForm = () => {
    setStudentForm(initialStudentForm);
    setEditingStudentId("");
  };

  const handleEnrollStudent = async () => {
    if (!selectedStudent || !enrollCourseId) return;

    // Guard: prevent duplicate enrollment before hitting the API
    const alreadyEnrolled = selectedStudent.purchased_courses?.some(
      pc => String(pc) === String(enrollCourseId)
    );
    if (alreadyEnrolled) {
      setStudentStatus({ type: "error", message: "This student is already enrolled in the selected course." });
      setEnrollCourseId("");
      return;
    }

    try {
      await enrollStudentInCourse(selectedStudent._id, enrollCourseId);
      // Refresh student details locally
      const updatedStudents = await getStudentsList();
      setStudents(updatedStudents);
      const studentMatch = updatedStudents.find(s => s._id === selectedStudent._id);
      if (studentMatch) setSelectedStudent(studentMatch);
      setEnrollCourseId("");
      setStudentStatus({ type: "success", message: "Student enrolled successfully in course." });
    } catch (error) {
      setStudentStatus({ type: "error", message: error?.response?.data?.error || "Failed to enroll student." });
    }
  };

  const handleUnenrollStudent = async (courseId) => {
    if (!selectedStudent || !courseId) return;
    if (!window.confirm("Drop this student from this course? Progress data will be deleted.")) return;
    try {
      await removeStudentFromCourse(selectedStudent._id, courseId);
      const updatedStudents = await getStudentsList();
      setStudents(updatedStudents);
      const studentMatch = updatedStudents.find(s => s._id === selectedStudent._id);
      if (studentMatch) setSelectedStudent(studentMatch);
      // Remove progress state
      setStudentCoursesProgress(prev => {
        const copy = { ...prev };
        delete copy[courseId];
        return copy;
      });
    } catch (error) {
      alert("Failed to unenroll student.");
    }
  };

  const loadStudentProgressForCourse = async (courseId) => {
    if (!selectedStudent) return;
    setProgressLoadingMap(prev => ({ ...prev, [courseId]: true }));
    try {
      const data = await getStudentProgress(selectedStudent._id, courseId);
      setStudentCoursesProgress(prev => ({ ...prev, [courseId]: data }));
    } catch (error) {
      console.error("Failed to load progress:", error);
    } finally {
      setProgressLoadingMap(prev => ({ ...prev, [courseId]: false }));
    }
  };

  const filteredStudents = useMemo(() => {
    if (!studentSearch.trim()) return students;
    const term = studentSearch.toLowerCase();
    return students.filter(s =>
      String(s.name || "").toLowerCase().includes(term) ||
      String(s.email || "").toLowerCase().includes(term) ||
      String(s.phone_number || "").toLowerCase().includes(term)
    );
  }, [students, studentSearch]);

  const isDemoCourse = (c) => {
    const title = String(c?.title || "").toLowerCase();
    const price = String(c?.price || "").replace(/[₹,\s]/g, "");
    return (
      c?.is_demo === true ||
      title.includes("demo") ||
      title.includes("limited") ||
      price === "0" ||
      price === ""
    );
  };

  const demoCourses = useMemo(() =>
    courses.filter(isDemoCourse),
    [courses]
  );

  const activeCourses = useMemo(() =>
    courses.filter(c => !isDemoCourse(c)),
    [courses]
  );

  const unreadEnquiries = useMemo(() =>
    enquiries.filter(e => !e.read),
    [enquiries]
  );


  // ----------------------------------------------------
  // Inquiry Inbox Operations
  // ----------------------------------------------------
  const onDeleteEnquiry = async (id) => {
    if (!id) return;
    if (!window.confirm("Remove this enquiry request?")) return;
    try {
      await deleteEnquiry(id);
      await refreshData();
      setEnquiryStatus({ type: "success", message: "Enquiry removed." });
    } catch (error) {
      setEnquiryStatus({ type: "error", message: "Failed to delete enquiry." });
    }
  };

  const handleToggleEnquiryRead = async (enq) => {
    const newRead = !enq.read;
    // Optimistic update
    setEnquiries(prev => prev.map(e => e._id === enq._id ? { ...e, read: newRead } : e));
    try {
      await markEnquiryRead(enq._id, newRead);
    } catch {
      // Revert on failure
      setEnquiries(prev => prev.map(e => e._id === enq._id ? { ...e, read: !newRead } : e));
    }
  };

  const handleMarkAllRead = async () => {
    setEnquiries(prev => prev.map(e => ({ ...e, read: true })));
    try {
      await markAllEnquiriesRead();
      setEnquiryStatus({ type: "success", message: "All enquiries marked as read." });
    } catch {
      setEnquiryStatus({ type: "error", message: "Failed to mark all as read. Please refresh." });
    }
  };


  // ----------------------------------------------------
  // Blogs Operations
  // ----------------------------------------------------
  const handleBlogChange = (e) => {
    const { name, value } = e.target;
    setBlogForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlogFile = (e) => {
    const file = e.target.files?.[0] || null;
    setBlogForm((prev) => ({ ...prev, cover_image: file }));
  };

  const submitBlog = async (e) => {
    e.preventDefault();
    setBlogStatus({ type: "", message: "" });
    setIsSubmittingBlog(true);

    if (!editingBlogId && !blogForm.cover_image) {
      setBlogStatus({
        type: "error",
        message: "Blog cover image is required for new blog creation.",
      });
      setIsSubmittingBlog(false);
      return;
    }

    if (!blogForm.content.trim()) {
      setBlogStatus({ type: "error", message: "Blog content is required." });
      setIsSubmittingBlog(false);
      return;
    }

    if (!Array.isArray(blogForm.category) || blogForm.category.length === 0) {
      setBlogStatus({ type: "error", message: "At least one category is required." });
      setIsSubmittingBlog(false);
      return;
    }

    try {
      const blogPayload = {
        ...blogForm,
        category: dedupeByLowerCase(blogForm.category),
        sub_category: dedupeByLowerCase(blogForm.sub_category),
        cover_image: blogForm.cover_image,
      };

      if (editingBlogId) {
        await updateBlog(editingBlogId, blogPayload);
        setBlogStatus({ type: "success", message: "Blog updated successfully!" });
      } else {
        await createBlog(blogPayload);
        setBlogStatus({ type: "success", message: "Blog created successfully!" });
      }
      resetBlogForm();
      await refreshData();
    } catch (error) {
      setBlogStatus({
        type: "error",
        message: error?.response?.data?.message || "Blog operation failed.",
      });
    } finally {
      setIsSubmittingBlog(false);
    }
  };

  const onEditBlog = async (blog) => {
    const id = getEntityId(blog);
    if (!id) return;
    const initialMapped = mapBlogToForm(blog);

    setEditingBlogId(id);
    setBlogForm(initialMapped);
    setBlogImagePreviewError(false);
    setActiveTab("blogs");

    try {
      const fullBlog = await getBlogById(id);
      const mapped = mapBlogToForm(fullBlog);
      setBlogForm(mapped);
    } catch (error) {
      setBlogStatus({
        type: "error",
        message: "Loaded partial blog details for editing.",
      });
    }
  };

  const onDeleteBlog = async (id) => {
    if (!id) return;
    if (!window.confirm("Delete this blog post?")) return;
    try {
      await deleteBlog(id);
      await refreshData();
      setBlogStatus({ type: "success", message: "Blog post deleted." });
    } catch (error) {
      setBlogStatus({
        type: "error",
        message: error?.response?.data?.message || "Blog delete failed.",
      });
    }
  };

  const resetBlogForm = () => {
    setBlogForm(initialBlogForm);
    setEditingBlogId("");
    setBlogImagePreviewError(false);
  };

  // ----------------------------------------------------
  // Blog Category / Tag Handlers (were previously missing)
  // ----------------------------------------------------
  const handleAddCategory = (val) => {
    const normalized = normalizeCategoryValue(val);
    if (!normalized) return;
    setBlogForm((prev) => ({ ...prev, category: addUniqueValue(prev.category, normalized) }));
    setCategoryDraft("");
    setIsAddingNewCategory(false);
  };

  const handleRemoveCategory = (cat) => {
    setBlogForm((prev) => ({ ...prev, category: removeValue(prev.category, cat) }));
  };

  const handleAddSubCategory = (val) => {
    const normalized = normalizeCategoryValue(val);
    if (!normalized) return;
    setBlogForm((prev) => ({ ...prev, sub_category: addUniqueValue(prev.sub_category, normalized) }));
    setSubCategoryDraft("");
    setIsAddingNewSubCategory(false);
  };

  const handleRemoveSubCategory = (sub) => {
    setBlogForm((prev) => ({ ...prev, sub_category: removeValue(prev.sub_category, sub) }));
  };

  const onOpenBlogDetails = (id) => {
    const blog = blogs.find((b) => getEntityId(b) === id);
    if (blog) onEditBlog(blog);
  };

  // ----------------------------------------------------
  // Rendering Helpers
  // ----------------------------------------------------
  const statsOverview = useMemo(() => {
    return [
      { label: "Total Students", count: students.length, icon: Users, color: "from-blue-500 to-indigo-600" },
      { label: "Active Courses", count: courses.length, icon: Books, color: "from-emerald-400 to-teal-600" },
      { label: "Unread Enquiries", count: unreadEnquiries.length, icon: EnvelopeOpen, color: "from-orange-400 to-red-500", total: enquiries.length },
      { label: "Blog Posts", count: blogs.length, icon: Article, color: "from-fuchsia-500 to-pink-600" },
    ];
  }, [students, courses, enquiries, unreadEnquiries, blogs]);

  if (isCheckingAccess) {
    return (
      <section className="w-full min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 border-4 border-[#0D6FEA] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400 text-sm font-medium">Authorizing Admin Console Access...</p>
        </div>
      </section>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex font-sans" data-theme={darkMode ? "dark" : "light"}>
      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
        />
      )}

      {/* Sidebar Navigation */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 w-64 border-r border-slate-800 bg-slate-900/60 backdrop-blur-xl flex flex-col justify-between p-6 z-50 transition-transform duration-300 ease-in-out
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
      >
        <div>
          <div className="flex items-center justify-between gap-3 mb-10 px-2">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-tr from-blue-500 to-indigo-600 flex items-center justify-center font-bold text-white text-lg flex-shrink-0">
                S
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-extrabold text-white tracking-wide">SOFAR</h2>
                <p className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold">Console</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setDarkMode((prev) => !prev)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 transition-all cursor-pointer flex-shrink-0"
                title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
              >
                {darkMode ? <Sun size={17} /> : <Moon size={17} />}
              </button>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 transition-all cursor-pointer flex-shrink-0"
                title="Close Sidebar"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          <nav className="space-y-1.5">
            {[
              { id: "overview", label: "Overview", icon: ChartPie },
              { id: "courses", label: "Courses", icon: Books },
              { id: "students", label: "Students", icon: Users },
              { id: "enquiries", label: "Enquiries", icon: EnvelopeOpen },
              { id: "blogs", label: "Blogs", icon: Article },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setCourseStatus({ type: "", message: "" });
                    setBlogStatus({ type: "", message: "" });
                    setStudentStatus({ type: "", message: "" });
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 text-sm font-medium cursor-pointer
                    ${
                      activeTab === tab.id
                        ? "bg-[#0D6FEA] text-white shadow-lg shadow-blue-500/25"
                        : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50"
                    }`}
                >
                  <Icon size={20} weight={activeTab === tab.id ? "fill" : "regular"} />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-all duration-300 text-sm font-medium cursor-pointer"
        >
          <SignOut size={20} />
          Logout
        </button>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 overflow-y-auto p-4 sm:p-8 lg:p-12">
        <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 border-b border-slate-800/80 pb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-xl bg-slate-900 border border-slate-800 text-slate-100 hover:bg-slate-800 cursor-pointer"
              title="Open Navigation"
            >
              <List size={22} />
            </button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight capitalize">
                {activeTab === "enquiries" ? "Inquiry Inbox" : `${activeTab} Management`}
              </h1>
              <p className="text-slate-400 text-xs mt-1">Manage and track your portal entities, syllabus streams, and system metrics.</p>
            </div>
          </div>
          <button
            onClick={refreshData}
            disabled={isLoadingData}
            className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-800 border border-slate-700 text-slate-300 hover:bg-slate-700 cursor-pointer disabled:opacity-50 w-fit"
          >
            <ArrowClockwise className={isLoadingData ? "animate-spin" : ""} size={16} />
            Sync Data
          </button>
        </header>

        {/* 1. OVERVIEW SCREEN */}
        {activeTab === "overview" && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {statsOverview.map((stat, i) => {
                const Icon = stat.icon;
                return (
                  <div key={i} className="bg-slate-900/60 border border-slate-800/60 rounded-2xl p-6 relative overflow-hidden group shadow-md">
                    <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-tr ${stat.color} opacity-5 blur-2xl group-hover:opacity-10 transition-opacity duration-500`} />
                    <div className="flex justify-between items-center mb-4">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-tr ${stat.color} flex items-center justify-center text-white shadow-md`}>
                        <Icon size={24} weight="bold" />
                      </div>
                    </div>
                    <p className="text-slate-400 text-sm font-semibold">{stat.label}</p>
                    <h3 className="text-3xl font-black text-white mt-1">{isLoadingData ? "..." : stat.count}</h3>
                    {stat.total !== undefined && (
                      <p className="text-[10px] text-slate-600 mt-1 font-mono">{stat.total} total</p>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Quick info about Local Transcoding */}
              <div className="bg-slate-900/60 border border-slate-800/60 rounded-2xl p-6 shadow-md">
                <h3 className="text-lg font-bold text-white mb-3">Local Video Transcoding & Processing</h3>
                <p className="text-sm text-slate-300 leading-relaxed mb-4">
                  To avoid overloading the production servers with heavy CPU rendering, all course videos must be processed locally on your administrator machine.
                </p>
                <div className="bg-slate-950 p-4 rounded-xl border border-slate-800 text-xs font-mono text-blue-400 leading-relaxed space-y-2">
                  <p className="text-gray-500"># Run the local transcoder utility from your local backend directory:</p>
                  <p className="text-white font-bold">python scripts/local_transcoder.py</p>
                  <p className="text-gray-500"># This script automatically transcodes local MP4 videos to multi-bitrate HLS and uploads them to S3. Copy-paste the resulting CloudFront master.m3u8 URL directly into the Syllabus Builder.</p>
                </div>
              </div>

              {/* Developer system panel */}
              <div className="bg-slate-900/60 border border-slate-800/60 rounded-2xl p-6 shadow-md flex flex-col justify-between">
                <div>
                  <h3 className="text-lg font-bold text-white mb-2">Portal Quick Links</h3>
                  <p className="text-sm text-slate-400 mb-4">Jump directly to your active client application routes.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <a href="/" target="_blank" className="flex items-center justify-between p-3.5 bg-slate-950 hover:bg-slate-800 border border-slate-800 rounded-xl transition-all text-xs font-semibold text-slate-300 group">
                    Home Page
                    <ArrowSquareOut size={16} className="text-slate-500 group-hover:text-slate-300" />
                  </a>
                  <a href="/dashboard" target="_blank" className="flex items-center justify-between p-3.5 bg-slate-950 hover:bg-slate-800 border border-slate-800 rounded-xl transition-all text-xs font-semibold text-slate-300 group">
                    Student Portal
                    <ArrowSquareOut size={16} className="text-slate-500 group-hover:text-slate-300" />
                  </a>
                  <a href="/courses" target="_blank" className="flex items-center justify-between p-3.5 bg-slate-950 hover:bg-slate-800 border border-slate-800 rounded-xl transition-all text-xs font-semibold text-slate-300 group">
                    All Courses
                    <ArrowSquareOut size={16} className="text-slate-500 group-hover:text-slate-300" />
                  </a>
                  <a href="/blogs" target="_blank" className="flex items-center justify-between p-3.5 bg-slate-950 hover:bg-slate-800 border border-slate-800 rounded-xl transition-all text-xs font-semibold text-slate-300 group">
                    Blog Portal
                    <ArrowSquareOut size={16} className="text-slate-500 group-hover:text-slate-300" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 2. COURSES MANAGEMENT & SYLLABUS BUILDER */}
        {activeTab === "courses" && (
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 items-start">
            {/* Create/Edit Course form + Syllabus Builder */}
            <div className="xl:col-span-8 bg-slate-900/60 border border-slate-800/60 rounded-2xl p-6 lg:p-8 shadow-md">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <Books size={24} className="text-[#0D6FEA]" />
                {editingCourseId ? `Modify: ${courseForm.title}` : "Create New Course"}
              </h2>

              <form onSubmit={submitCourse} className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400 border-b border-slate-800 pb-2">1. Basic Info</h3>
                  <input
                    name="title"
                    value={courseForm.title}
                    onChange={handleCourseChange}
                    placeholder="Course Name / Title"
                    className="w-full bg-slate-950 border border-slate-800 focus:border-[#0D6FEA] rounded-xl px-4 py-3 text-slate-100 outline-none transition-colors"
                    required
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <textarea
                      name="description"
                      rows={3}
                      value={courseForm.description}
                      onChange={handleCourseChange}
                      placeholder="Detailed Course Description"
                      className="w-full bg-slate-950 border border-slate-800 focus:border-[#0D6FEA] rounded-xl px-4 py-3 text-slate-100 outline-none transition-colors"
                    />
                    <textarea
                      name="short_description"
                      rows={3}
                      value={courseForm.short_description}
                      onChange={handleCourseChange}
                      placeholder="Short Card Summary"
                      className="w-full bg-slate-950 border border-slate-800 focus:border-[#0D6FEA] rounded-xl px-4 py-3 text-slate-100 outline-none transition-colors"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400 border-b border-slate-800 pb-2">2. Pricing & Schedule</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <input
                      name="price"
                      type="number"
                      value={courseForm.price}
                      onChange={handleCourseChange}
                      placeholder="Price (₹)"
                      className="w-full bg-slate-950 border border-slate-800 focus:border-[#0D6FEA] rounded-xl px-4 py-3 text-slate-100 outline-none transition-colors"
                      required
                    />
                    <input
                      name="discount_price"
                      type="number"
                      value={courseForm.discount_price}
                      onChange={handleCourseChange}
                      placeholder="Discount Price (₹)"
                      className="w-full bg-slate-950 border border-slate-800 focus:border-[#0D6FEA] rounded-xl px-4 py-3 text-slate-100 outline-none transition-colors"
                      disabled={!courseForm.is_discounted}
                    />
                    <div className="flex items-center px-2">
                      <label className="flex items-center gap-2 text-sm text-slate-300 select-none cursor-pointer">
                        <input
                          type="checkbox"
                          name="is_discounted"
                          checked={courseForm.is_discounted}
                          onChange={handleCourseChange}
                          className="w-4.5 h-4.5 accent-blue-600 rounded"
                        />
                        Apply Discount Price
                      </label>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs text-slate-400 font-medium px-1">Starting Date</label>
                      <input
                        name="starting_date"
                        type="datetime-local"
                        value={courseForm.starting_date}
                        onChange={handleCourseChange}
                        className="w-full bg-slate-950 border border-slate-800 focus:border-[#0D6FEA] rounded-xl px-4 py-3 text-slate-100 outline-none transition-colors"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs text-slate-400 font-medium px-1">Batch Schedule Time</label>
                      <input
                        name="batch_time"
                        value={courseForm.batch_time}
                        onChange={handleCourseChange}
                        placeholder="e.g. 7:00 PM - 9:00 PM IST"
                        className="w-full bg-slate-950 border border-slate-800 focus:border-[#0D6FEA] rounded-xl px-4 py-3 text-slate-100 outline-none transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      name="batch_type"
                      value={courseForm.batch_type}
                      onChange={handleCourseChange}
                      placeholder="Batch Type (e.g. Weekend Cohort)"
                      className="w-full bg-slate-950 border border-slate-800 focus:border-[#0D6FEA] rounded-xl px-4 py-3 text-slate-100 outline-none transition-colors"
                    />
                    <input
                      name="batch_days"
                      value={courseForm.batch_days}
                      onChange={handleCourseChange}
                      placeholder="Batch Days (e.g. Saturday, Sunday)"
                      className="w-full bg-slate-950 border border-slate-800 focus:border-[#0D6FEA] rounded-xl px-4 py-3 text-slate-100 outline-none transition-colors"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400 border-b border-slate-800 pb-2">3. Features & Images</h3>
                  <textarea
                    name="essential_features"
                    rows={3}
                    value={courseForm.essential_features}
                    onChange={handleCourseChange}
                    placeholder="Key features / highlights (Enter one feature per line)"
                    className="w-full bg-slate-950 border border-slate-800 focus:border-[#0D6FEA] rounded-xl px-4 py-3 text-slate-100 outline-none transition-colors"
                    required
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                    <div className="space-y-2">
                      <label className="text-xs text-slate-400 font-medium px-1">Course Cover Image</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleCourseFile}
                        className="w-full text-xs text-slate-400 file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-500 cursor-pointer"
                      />
                    </div>
                    {courseForm.cover_image_url && (
                      <div className="h-28 relative rounded-xl border border-slate-800 overflow-hidden shadow-inner">
                        <img
                          src={courseForm.cover_image_url}
                          alt="Course cover preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex gap-6 py-2 px-1">
                    <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        name="few_seats_left"
                        checked={courseForm.few_seats_left}
                        onChange={handleCourseChange}
                        className="w-4.5 h-4.5 accent-blue-600 rounded"
                      />
                      Show 'Few Seats Left' Badge
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        name="display"
                        checked={courseForm.display}
                        onChange={handleCourseChange}
                        className="w-4.5 h-4.5 accent-blue-600 rounded"
                      />
                      Publish and Display Publicly
                    </label>
                  </div>
                </div>

                {/* 4. SYLLABUS BUILDER SECTION */}
                <div className="space-y-4 pt-4 border-t border-slate-800/80">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400 border-b border-slate-800 pb-2">4. Syllabus / Course Sections</h3>
                  
                  {/* Local state Syllabus List */}
                  <div className="space-y-4">
                    {courseSyllabus.length === 0 ? (
                      <p className="text-xs text-slate-500 py-2">Syllabus is empty. Add a section below to get started.</p>
                    ) : (
                      courseSyllabus.map((subtitle, secIndex) => (
                        <div key={secIndex} className="bg-slate-950 border border-slate-800/80 rounded-xl p-4 space-y-3">
                          <div className="flex justify-between items-center border-b border-slate-800 pb-2.5">
                            <div className="flex items-center gap-2">
                              <Folder size={18} className="text-[#0D6FEA]" />
                              <span className="font-semibold text-sm text-slate-200">{subtitle.title}</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeSyllabusSection(secIndex)}
                              className="text-red-400 hover:text-red-300 text-xs font-semibold cursor-pointer"
                            >
                              Remove Section
                            </button>
                          </div>

                          {/* Section Videos List */}
                          <div className="space-y-2 pl-4">
                            <div className="flex justify-between items-center">
                              <span className="text-xs font-bold text-slate-400">Videos ({subtitle.videos?.length || 0})</span>
                              <button
                                type="button"
                                onClick={() => addVideoToSection(secIndex)}
                                className="text-blue-400 hover:text-blue-300 text-xs font-semibold flex items-center gap-0.5 cursor-pointer"
                              >
                                <Plus size={14} /> Add HLS Video
                              </button>
                            </div>
                            {(!subtitle.videos || subtitle.videos.length === 0) ? (
                              <p className="text-[10px] text-slate-600">No videos added yet.</p>
                            ) : (
                              subtitle.videos.map((vid, vIdx) => (
                                <div key={vIdx} className="bg-slate-900 border border-slate-800/40 rounded-lg p-2.5 flex justify-between items-center text-xs">
                                  <div className="truncate pr-4 flex items-center gap-2">
                                    <Video size={14} className="text-slate-400" />
                                    <div>
                                      <p className="font-medium text-slate-300">{vid.title}</p>
                                      <p className="text-[10px] text-slate-500 font-mono truncate max-w-md">{vid.path}</p>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeVideoFromSection(secIndex, vIdx)}
                                    className="text-slate-500 hover:text-red-400 cursor-pointer"
                                  >
                                    <Trash size={16} />
                                  </button>
                                </div>
                              ))
                            )}
                          </div>

                          {/* Section Documents List */}
                          <div className="space-y-2 pl-4 pt-2">
                            <div className="flex justify-between items-center">
                              <span className="text-xs font-bold text-slate-400">Documents ({subtitle.documents?.length || 0})</span>
                              <button
                                type="button"
                                onClick={() => openPdfPanel(secIndex)}
                                className="text-blue-400 hover:text-blue-300 text-xs font-semibold flex items-center gap-0.5 cursor-pointer"
                              >
                                <Plus size={14} /> {pdfPanel.openIndex === secIndex ? "Close Panel" : "Add PDF"}
                              </button>
                            </div>

                            {/* Existing documents list */}
                            {(!subtitle.documents || subtitle.documents.length === 0) ? (
                              <p className="text-[10px] text-slate-600">No documents added yet.</p>
                            ) : (
                              subtitle.documents.map((doc, dIdx) => (
                                <div key={dIdx} className="bg-slate-900 border border-slate-800/40 rounded-lg p-2.5 flex justify-between items-center text-xs">
                                  <div className="truncate pr-4 flex items-center gap-2">
                                    <FilePdf size={14} className="text-red-400" />
                                    <div>
                                      <p className="font-medium text-slate-300">{doc.title}</p>
                                      <p className="text-[10px] text-slate-500 font-mono truncate max-w-md">{doc.path}</p>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeDocumentFromSection(secIndex, dIdx)}
                                    className="text-slate-500 hover:text-red-400 cursor-pointer"
                                  >
                                    <Trash size={16} />
                                  </button>
                                </div>
                              ))
                            )}

                            {/* ── Inline PDF Upload Panel ── */}
                            {pdfPanel.openIndex === secIndex && (
                              <div className="mt-3 bg-slate-900 border border-blue-500/30 rounded-xl p-4 space-y-3 shadow-lg">
                                <p className="text-xs font-bold text-blue-400 uppercase tracking-wider">Add PDF Document</p>

                                {/* Document Title */}
                                <input
                                  type="text"
                                  placeholder="Document Title (e.g. Week 1 Notes)"
                                  value={pdfPanel.title}
                                  onChange={(e) => setPdfPanel((prev) => ({ ...prev, title: e.target.value }))}
                                  className="w-full bg-slate-950 border border-slate-700 focus:border-blue-500 rounded-lg px-3 py-2 text-xs text-slate-100 outline-none transition-colors"
                                />

                                {/* Mode Toggle */}
                                <div className="flex gap-1 bg-slate-950 border border-slate-800 rounded-lg p-1">
                                  <button
                                    type="button"
                                    onClick={() => setPdfPanel((prev) => ({ ...prev, uploadMode: "file", url: "", uploadSuccess: false, uploadError: "", file: null }))}
                                    className={`flex-1 py-1.5 rounded-md text-[10px] font-bold transition-all cursor-pointer ${
                                      pdfPanel.uploadMode === "file"
                                        ? "bg-blue-600 text-white"
                                        : "text-slate-400 hover:text-slate-200"
                                    }`}
                                  >
                                    📁 Upload PDF File
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setPdfPanel((prev) => ({ ...prev, uploadMode: "link", uploadSuccess: false, uploadError: "", file: null }))}
                                    className={`flex-1 py-1.5 rounded-md text-[10px] font-bold transition-all cursor-pointer ${
                                      pdfPanel.uploadMode === "link"
                                        ? "bg-blue-600 text-white"
                                        : "text-slate-400 hover:text-slate-200"
                                    }`}
                                  >
                                    🔗 Paste S3 / CloudFront Link
                                  </button>
                                </div>

                                {/* File Upload Mode */}
                                {pdfPanel.uploadMode === "file" && (
                                  <div className="space-y-2">
                                    <input
                                      type="file"
                                      accept=".pdf,application/pdf"
                                      disabled={pdfPanel.uploading}
                                      onChange={(e) => {
                                        const f = e.target.files?.[0] || null;
                                        setPdfPanel((prev) => ({ ...prev, file: f, url: "", uploadSuccess: false, uploadError: "" }));
                                      }}
                                      className="w-full text-xs text-slate-400 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-500 cursor-pointer disabled:opacity-50"
                                    />
                                    <button
                                      type="button"
                                      onClick={handlePdfUpload}
                                      disabled={!pdfPanel.file || pdfPanel.uploading || pdfPanel.uploadSuccess}
                                      className="w-full py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-xs font-bold text-white transition-colors cursor-pointer flex items-center justify-center gap-2"
                                    >
                                      {pdfPanel.uploading ? (
                                        <>
                                          <span className="inline-block w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                          Uploading to S3...
                                        </>
                                      ) : pdfPanel.uploadSuccess ? (
                                        "✅ Uploaded — ready to add"
                                      ) : (
                                        "Upload to S3"
                                      )}
                                    </button>
                                    {pdfPanel.uploadSuccess && (
                                      <p className="text-[10px] text-green-400 font-mono break-all">{pdfPanel.url}</p>
                                    )}
                                    {pdfPanel.uploadError && (
                                      <p className="text-[10px] text-red-400">{pdfPanel.uploadError}</p>
                                    )}
                                  </div>
                                )}

                                {/* Paste Link Mode */}
                                {pdfPanel.uploadMode === "link" && (
                                  <input
                                    type="url"
                                    placeholder="https://cdn.thesofar.org/..."
                                    value={pdfPanel.url}
                                    onChange={(e) => setPdfPanel((prev) => ({ ...prev, url: e.target.value }))}
                                    className="w-full bg-slate-950 border border-slate-700 focus:border-blue-500 rounded-lg px-3 py-2 text-xs text-slate-100 font-mono outline-none transition-colors"
                                  />
                                )}

                                {/* Action Buttons */}
                                <div className="flex gap-2 pt-1">
                                  <button
                                    type="button"
                                    onClick={() => handleAddDocument(secIndex)}
                                    disabled={!pdfPanel.title.trim() || !pdfPanel.url.trim() || pdfPanel.uploading}
                                    className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-xs font-bold text-white transition-colors cursor-pointer"
                                  >
                                    Add to Section
                                  </button>
                                  <button
                                    type="button"
                                    onClick={closePdfPanel}
                                    className="px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-xs font-bold text-slate-400 hover:text-slate-300 transition-colors cursor-pointer"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Add New Section Controls */}
                  <div className="flex gap-2 bg-slate-950 border border-slate-800/80 p-3.5 rounded-xl">
                    <input
                      value={sectionTitleDraft}
                      onChange={(e) => setSectionTitleDraft(e.target.value)}
                      placeholder="New Section Name (e.g. Lesson 1: Basics)"
                      className="flex-1 bg-slate-900 border border-slate-800 focus:border-[#0D6FEA] rounded-lg px-3.5 py-2 text-xs outline-none"
                    />
                    <button
                      type="button"
                      onClick={addSyllabusSection}
                      className="px-4 py-2 bg-slate-800 border border-slate-700 text-xs font-bold text-slate-200 hover:bg-slate-700 rounded-lg flex items-center gap-1 cursor-pointer"
                    >
                      <Plus size={14} /> Add Section
                    </button>
                  </div>
                </div>

                {courseStatus.message && (
                  <p className={`text-sm ${courseStatus.type === "success" ? "text-green-400" : "text-red-400"}`}>
                    {courseStatus.message}
                  </p>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={isSubmittingCourse}
                    className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-xs font-bold text-white disabled:opacity-60 cursor-pointer"
                  >
                    {isSubmittingCourse ? "Saving..." : editingCourseId ? "Update Course" : "Create Course"}
                  </button>
                  {editingCourseId && (
                    <button
                      type="button"
                      onClick={resetCourseForm}
                      className="px-5 py-2.5 rounded-xl border border-slate-700 text-xs font-bold text-slate-400 hover:text-slate-300 cursor-pointer"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>

            {/* Courses sidebar list — tabbed Demo / Active */}
            <div className="xl:col-span-4 bg-slate-900/60 border border-slate-800/60 rounded-2xl p-6 shadow-md">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold text-white">Course Catalog</h2>
                <span className="text-xs text-slate-500 font-mono">{courses.length} total</span>
              </div>

              {/* Demo / Active tab switcher */}
              <div className="flex gap-1 mb-4 bg-slate-950 border border-slate-800 rounded-xl p-1">
                <button
                  onClick={() => setCourseCatalogTab("active")}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    courseCatalogTab === "active"
                      ? "bg-blue-600 text-white shadow"
                      : "text-slate-400 hover:text-slate-200"
                  }`}
                >
                  Active ({activeCourses.length})
                </button>
                <button
                  onClick={() => setCourseCatalogTab("demo")}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    courseCatalogTab === "demo"
                      ? "bg-emerald-600 text-white shadow"
                      : "text-slate-400 hover:text-slate-200"
                  }`}
                >
                  Demo ({demoCourses.length})
                </button>
              </div>

              <div className="space-y-3 max-h-[680px] overflow-y-auto pr-1">
                {(courseCatalogTab === "active" ? activeCourses : demoCourses).length === 0 ? (
                  <p className="text-sm text-slate-500 py-6 text-center">
                    No {courseCatalogTab} courses found.
                  </p>
                ) : (
                  (courseCatalogTab === "active" ? activeCourses : demoCourses).map((course) => {
                    const id = getEntityId(course);
                    const isDemo = Number(course?.price || 0) === 0 || course?.is_demo === true;
                    return (
                      <div key={id} className="border border-slate-800 bg-slate-950 p-4 rounded-xl flex flex-col justify-between gap-3.5 hover:border-slate-700 transition-colors">
                        <div>
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-bold text-slate-200 text-sm leading-snug">{course?.title || "Untitled"}</p>
                            {isDemo && (
                              <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 flex-shrink-0 border border-emerald-500/20">
                                Free
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-1 font-mono">
                            {isDemo ? "Free / Demo" : `₹ ${Number(course?.price || 0).toLocaleString()}`}
                          </p>
                          <p className="text-[10px] text-[#0D6FEA] font-semibold mt-1.5 uppercase tracking-wide">
                            {course?.subtitles?.length || 0} Sections
                          </p>
                        </div>
                        <div className="flex gap-2 border-t border-slate-800/60 pt-3">
                          <button
                            onClick={() => onEditCourse(course)}
                            className="flex-1 px-3 py-1.5 text-xs font-bold rounded-lg bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 cursor-pointer text-center"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => onDeleteCourse(id)}
                            className="px-3 py-1.5 text-xs font-bold rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 cursor-pointer"
                          >
                            <Trash size={15} />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}

        {/* 3. STUDENTS ENROLLMENT & DETAILS */}
        {activeTab === "students" && (
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 items-start">
            {/* Student Search list */}
            <div className="xl:col-span-4 bg-slate-900/60 border border-slate-800/60 rounded-2xl p-6 shadow-md space-y-4">
              <div className="flex justify-between items-center border-b border-slate-850 pb-3">
                <h2 className="text-lg font-bold text-white">Registered Students</h2>
              </div>

              <input
                value={studentSearch}
                onChange={(e) => setStudentSearch(e.target.value)}
                placeholder="Search by name, email, phone..."
                className="w-full bg-slate-950 border border-slate-800 focus:border-[#0D6FEA] rounded-xl px-3.5 py-2.5 text-xs outline-none"
              />

              <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
                {filteredStudents.length === 0 ? (
                  <p className="text-xs text-slate-500 py-3">No students found.</p>
                ) : (
                  filteredStudents.map((student) => (
                    <div
                      key={student._id}
                      onClick={() => {
                        setSelectedStudent(student);
                        setStudentCoursesProgress({});
                        setEnrollCourseId("");
                        setStudentStatus({ type: "", message: "" });
                        resetStudentForm();
                      }}
                      className={`p-3.5 rounded-xl border transition-all cursor-pointer flex flex-col justify-between gap-1.5
                        ${
                          selectedStudent?._id === student._id
                            ? "bg-blue-600/10 border-blue-500/60 shadow-md"
                            : "bg-slate-950 border-slate-800 hover:border-slate-700"
                        }`}
                    >
                      <div className="flex justify-between items-start gap-2">
                        <p className="font-bold text-slate-200 text-xs truncate">{student.name || "Unnamed Student"}</p>
                        <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded
                          ${
                            student.role === "admin"
                              ? "bg-red-500/25 text-red-400"
                              : student.role === "faculty"
                                ? "bg-amber-500/25 text-amber-400"
                                : "bg-blue-500/25 text-blue-400"
                          }`}>
                          {student.role || "student"}
                        </span>
                      </div>
                      <p className="text-[10px] text-slate-500 truncate">{student.email}</p>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Student detail panel */}
            <div className="xl:col-span-8 space-y-6">
              {/* Profile Details Card */}
              {selectedStudent ? (
                <>
                  <div className="bg-slate-900/60 border border-slate-800/60 rounded-2xl p-6 shadow-md space-y-6">
                    <div className="flex justify-between items-start border-b border-slate-800 pb-4">
                      <div>
                        <h2 className="text-xl font-bold text-white flex items-center gap-2">
                          <User size={20} className="text-[#0D6FEA]" />
                          {selectedStudent.name || "Student Details"}
                        </h2>
                        <p className="text-xs text-slate-400 mt-1 font-mono">ID: {selectedStudent._id}</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => onEditStudent(selectedStudent)}
                          className="px-3.5 py-1.5 text-xs font-bold rounded-lg bg-slate-800 border border-slate-700 text-slate-300 hover:bg-slate-700 cursor-pointer"
                        >
                          Modify Profile
                        </button>
                        <button
                          onClick={() => onDeleteStudent(selectedStudent._id)}
                          className="px-3.5 py-1.5 text-xs font-bold rounded-lg bg-red-600/10 hover:bg-red-600/20 text-red-400 cursor-pointer"
                        >
                          Delete Account
                        </button>
                      </div>
                    </div>

                    {/* Edit Student profile form (shown when editingStudentId equals selectedStudent._id) */}
                    {editingStudentId === selectedStudent._id && (
                      <form onSubmit={submitStudent} className="bg-slate-950 border border-slate-800/80 p-4 rounded-xl space-y-4">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Edit Student Info</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <input
                            name="name"
                            value={studentForm.name}
                            onChange={handleStudentChange}
                            placeholder="Full Name"
                            className="bg-slate-900 border border-slate-800 focus:border-[#0D6FEA] rounded-lg px-3.5 py-2.5 text-xs outline-none"
                            required
                          />
                          <input
                            name="email"
                            type="email"
                            value={studentForm.email}
                            onChange={handleStudentChange}
                            placeholder="Email address"
                            className="bg-slate-900 border border-slate-800 focus:border-[#0D6FEA] rounded-lg px-3.5 py-2.5 text-xs outline-none"
                            required
                          />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <input
                            name="phone_number"
                            value={studentForm.phone_number}
                            onChange={handleStudentChange}
                            placeholder="Phone number"
                            className="bg-slate-900 border border-slate-800 focus:border-[#0D6FEA] rounded-lg px-3.5 py-2.5 text-xs outline-none"
                          />
                          <select
                            name="role"
                            value={studentForm.role}
                            onChange={handleStudentChange}
                            className="bg-slate-900 border border-slate-800 focus:border-[#0D6FEA] rounded-lg px-3.5 py-2.5 text-xs outline-none text-slate-300"
                          >
                            <option value="student">Student</option>
                            <option value="faculty">Faculty</option>
                            <option value="admin">Administrator</option>
                          </select>
                        </div>
                        {studentStatus.message && (
                          <p className={`text-xs ${studentStatus.type === "success" ? "text-green-400" : "text-red-400"}`}>
                            {studentStatus.message}
                          </p>
                        )}
                        <div className="flex gap-2">
                          <button
                            type="submit"
                            disabled={isSubmittingStudent}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-xs font-bold text-white cursor-pointer"
                          >
                            Save Updates
                          </button>
                          <button
                            type="button"
                            onClick={resetStudentForm}
                            className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-xs font-bold text-slate-400 hover:text-slate-300 cursor-pointer"
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    )}

                    {/* Enrollments & Progress Section */}
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400">Course Enrollments</h3>
                        
                        {/* Course enrollment drop container */}
                        <div className="flex gap-2 items-center">
                          <select
                            value={enrollCourseId}
                            onChange={(e) => setEnrollCourseId(e.target.value)}
                            className="bg-slate-950 border border-slate-800 rounded-lg px-3 py-1.5 text-xs text-slate-300 outline-none"
                          >
                            <option value="">Choose Course to Enroll...</option>
                            {courses
                              .filter(c => {
                                const cId = String(c?.id || c?._id || "");
                                return !selectedStudent.purchased_courses?.some(
                                  pc => String(pc) === cId
                                );
                              })
                              .map(c => (
                                <option key={getEntityId(c)} value={getEntityId(c)}>{c.title}</option>
                              ))}
                          </select>
                          <button
                            onClick={handleEnrollStudent}
                            disabled={!enrollCourseId}
                            className="px-3.5 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-xs font-bold text-white disabled:opacity-50 cursor-pointer flex items-center gap-1"
                          >
                            <UserCirclePlus size={16} /> Enroll
                          </button>
                        </div>
                      </div>

                      <div className="space-y-3">
                        {(!selectedStudent.purchased_courses || selectedStudent.purchased_courses.length === 0) ? (
                          <p className="text-xs text-slate-500 bg-slate-950 p-4 border border-slate-850 rounded-xl">Student is not enrolled in any courses yet.</p>
                        ) : (
                          selectedStudent.purchased_courses.map((courseId) => {
                            const courseMatch = courses.find(c => getEntityId(c) === courseId);
                            const progress = studentCoursesProgress[courseId];
                            const isLoadingProgress = progressLoadingMap[courseId];

                            return (
                              <div key={courseId} className="bg-slate-950 border border-slate-800/80 p-4 rounded-xl space-y-3.5">
                                <div className="flex justify-between items-start gap-4">
                                  <div className="flex items-center gap-2">
                                    <BookOpen size={16} className="text-[#0D6FEA]" />
                                    <span className="font-bold text-slate-200 text-sm leading-tight">
                                      {courseMatch?.title || "Unknown Course Course"}
                                    </span>
                                  </div>
                                  <div className="flex gap-2">
                                    {!progress && !isLoadingProgress && (
                                      <button
                                        onClick={() => loadStudentProgressForCourse(courseId)}
                                        className="text-xs text-[#0D6FEA] hover:underline font-semibold cursor-pointer"
                                      >
                                        Load progress tracker
                                      </button>
                                    )}
                                    {isLoadingProgress && (
                                      <span className="text-xs text-slate-500 animate-pulse font-semibold">Loading tracker...</span>
                                    )}
                                    <button
                                      onClick={() => handleUnenrollStudent(courseId)}
                                      className="text-xs text-red-400 hover:underline font-semibold cursor-pointer"
                                    >
                                      Drop Course
                                    </button>
                                  </div>
                                </div>

                                {/* Progress stats display */}
                                {progress && (
                                  <div className="space-y-3 pt-2.5 border-t border-slate-900/60">
                                    <div className="flex justify-between text-[11px] font-semibold text-slate-400">
                                      <span>Watch Progress: {progress.overall_progress_percentage}% complete</span>
                                      <span>({progress.completed_count} / {progress.total_count} videos watched)</span>
                                    </div>
                                    <div className="w-full bg-slate-900 h-1.5 rounded-full overflow-hidden border border-slate-800">
                                      <div
                                        className="bg-gradient-to-r from-blue-500 to-indigo-500 h-full rounded-full transition-all duration-300"
                                        style={{ width: `${progress.overall_progress_percentage}%` }}
                                      />
                                    </div>

                                    {/* Video list details breakdown */}
                                    <div className="pl-2 pt-1.5 space-y-1.5">
                                      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Videos breakdown</p>
                                      {Object.entries(progress.video_progress || {}).map(([key, record]) => {
                                        const [section, vidName] = key.split("|");
                                        const rec = record;
                                        return (
                                          <div key={key} className="flex justify-between items-center text-[10px] text-slate-400 pl-2">
                                            <span className="truncate max-w-xs font-medium">🎬 [{section}] {vidName}</span>
                                            <span className="font-semibold flex items-center gap-1 flex-shrink-0">
                                              {rec.completed ? (
                                                <span className="text-green-400 font-bold">✓ Complete</span>
                                              ) : rec.position_seconds > 0 ? (
                                                <span className="text-blue-400 font-medium">
                                                  Progress: {Math.round((rec.position_seconds / rec.duration_seconds) * 100)}%
                                                </span>
                                              ) : (
                                                <span className="text-slate-600">Unstarted</span>
                                              )}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="bg-slate-900/60 border border-slate-800/60 rounded-2xl p-12 text-center shadow-md">
                  <User size={48} className="text-slate-700 mx-auto mb-3" />
                  <p className="text-sm text-slate-400">Select a student from the sidebar list to view detailed course enrollments and progress tracking.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 4. ENQUIRY INBOX */}
        {activeTab === "enquiries" && (
          <div className="bg-slate-900/60 border border-slate-800/60 rounded-2xl p-6 lg:p-8 shadow-md">
            {/* Header */}
            <div className="flex flex-wrap justify-between items-center border-b border-slate-800 pb-4 mb-6 gap-3">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <EnvelopeOpen size={20} className="text-[#0D6FEA]" />
                Enquiries Inbox
                {unreadEnquiries.length > 0 && (
                  <span className="ml-1 inline-flex items-center justify-center w-5 h-5 text-[10px] font-extrabold rounded-full bg-red-500 text-white">
                    {unreadEnquiries.length}
                  </span>
                )}
              </h2>
              <div className="flex items-center gap-3">
                <span className="text-xs bg-blue-500/10 text-blue-400 font-bold py-1 px-3 rounded-full border border-blue-500/20">
                  {enquiries.length} total
                </span>
                {unreadEnquiries.length > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    className="text-xs font-bold py-1.5 px-3 rounded-full bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/20 cursor-pointer transition-all"
                  >
                    ✓ Mark All Read
                  </button>
                )}
              </div>
            </div>

            {enquiryStatus.message && (
              <p className={`text-sm mb-4 ${enquiryStatus.type === "success" ? "text-green-400" : "text-red-400"}`}>
                {enquiryStatus.message}
              </p>
            )}

            <div className="space-y-4">
              {enquiries.length === 0 ? (
                <p className="text-sm text-slate-500 py-10 text-center">No inquiries or login help requests found.</p>
              ) : (
                enquiries.map((enq) => (
                  <div
                    key={enq._id}
                    className={`border rounded-xl p-5 transition-colors space-y-3 relative group ${
                      enq.read
                        ? "bg-slate-950/60 border-slate-800/40"
                        : "bg-slate-950 border-slate-700 shadow-sm shadow-blue-500/5"
                    }`}
                  >
                    {/* Unread indicator dot */}
                    {!enq.read && (
                      <span className="absolute top-5 left-5 w-2 h-2 rounded-full bg-blue-500 ring-2 ring-blue-500/20" />
                    )}

                    {/* Actions row */}
                    <div className="absolute top-4 right-4 flex gap-2 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleToggleEnquiryRead(enq)}
                        title={enq.read ? "Mark as unread" : "Mark as read"}
                        className={`p-1.5 rounded-lg text-xs font-bold cursor-pointer transition-colors ${
                          enq.read
                            ? "bg-slate-800 hover:bg-slate-700 text-slate-400"
                            : "bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400"
                        }`}
                      >
                        {enq.read ? <Eye size={14} /> : <CheckCircle size={14} />}
                      </button>
                      <button
                        onClick={() => onDeleteEnquiry(enq._id)}
                        className="p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 cursor-pointer"
                      >
                        <Trash size={14} />
                      </button>
                    </div>

                    <div className={`flex flex-wrap items-center gap-3 ${!enq.read ? "pl-5" : ""}`}>
                      <span className={`text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full border
                        ${
                          enq.source === "login_help"
                            ? "bg-red-500/10 text-red-400 border-red-500/20"
                            : "bg-blue-500/10 text-blue-400 border-blue-500/20"
                        }`}>
                        {enq.source === "login_help" ? "Login Help" : "Contact Enquiry"}
                      </span>
                      {!enq.read && (
                        <span className="text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full bg-orange-500/10 text-orange-400 border border-orange-500/20">
                          New
                        </span>
                      )}
                      <span className="text-[10px] text-slate-500 font-mono">
                        {enq.timestamp ? new Date(enq.timestamp).toLocaleString() : "Date unknown"}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs border-b border-slate-900 pb-3">
                      <p className="text-slate-300 font-bold">👤 Name: <span className="font-normal text-slate-400 ml-1">{enq.name || "Not provided"}</span></p>
                      <p className="text-slate-300 font-bold">📞 Phone: <span className="font-normal text-slate-400 ml-1">{enq.number || "Not provided"}</span></p>
                      <p className="text-slate-300 font-bold">📧 Email: <span className="font-normal text-slate-400 ml-1">{enq.email || "Not provided"}</span></p>
                    </div>

                    {enq.message && (
                      <div className="text-xs pt-1">
                        <p className="text-slate-500 font-bold uppercase tracking-wide text-[9px] mb-1">Message Details</p>
                        <p className="text-slate-300 leading-relaxed bg-slate-900/60 p-3 rounded-lg border border-slate-900">{enq.message}</p>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* 5. BLOGS MANAGEMENT */}
        {activeTab === "blogs" && (
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 items-start">
            {/* Create/Edit Blog form */}
            <div className="xl:col-span-8 bg-slate-900/60 border border-slate-800/60 rounded-2xl p-6 lg:p-8 shadow-md">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <Article size={24} className="text-[#0D6FEA]" />
                {editingBlogId ? "Modify Blog Post" : "Compose New Blog Post"}
              </h2>

              <form onSubmit={submitBlog} className="space-y-5">
                <input
                  name="title"
                  value={blogForm.title}
                  onChange={handleBlogChange}
                  placeholder="Blog Article Title"
                  className="w-full bg-slate-950 border border-slate-800 focus:border-[#0D6FEA] rounded-xl px-4 py-3 text-slate-100 outline-none transition-colors"
                  required
                />
                <textarea
                  name="short_description"
                  rows={2}
                  value={blogForm.short_description}
                  onChange={handleBlogChange}
                  placeholder="Brief Excerpt / Summary"
                  className="w-full bg-slate-950 border border-slate-800 focus:border-[#0D6FEA] rounded-xl px-4 py-3 text-slate-100 outline-none transition-colors"
                />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Category Picker */}
                  <div className="flex flex-col gap-2">
                    <label className="text-xs text-slate-400 font-medium px-1">Categories</label>
                    {!isAddingNewCategory && blogCategories.length > 0 ? (
                      <select
                        value=""
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "__new__") {
                            setIsAddingNewCategory(true);
                            return;
                          }
                          handleAddCategory(val);
                        }}
                        className="w-full bg-slate-950 border border-slate-800 focus:border-[#0D6FEA] rounded-xl px-3 py-2 text-slate-300 outline-none text-xs"
                      >
                        <option value="">Assign existing category...</option>
                        {blogCategories.map((cat) => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                        <option value="__new__">+ Create new category</option>
                      </select>
                    ) : null}

                    {(isAddingNewCategory || blogCategories.length === 0) && (
                      <div className="flex gap-2">
                        <input
                          value={categoryDraft}
                          onChange={(e) => setCategoryDraft(e.target.value)}
                          placeholder="New category label"
                          className="w-full bg-slate-950 border border-slate-800 focus:border-[#0D6FEA] rounded-lg px-3 py-2 text-xs outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => handleAddCategory(categoryDraft)}
                          className="px-3 bg-blue-600 hover:bg-blue-500 rounded-lg text-xs font-bold text-white cursor-pointer"
                        >
                          Add
                        </button>
                        {blogCategories.length > 0 && (
                          <button
                            type="button"
                            onClick={() => {
                              setIsAddingNewCategory(false);
                              setCategoryDraft("");
                            }}
                            className="px-2 bg-slate-800 rounded-lg text-xs"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    )}

                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {blogForm.category.map((cat) => (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => handleRemoveCategory(cat)}
                          className="text-[10px] px-2.5 py-1 rounded-full border border-slate-800 bg-slate-900 text-slate-300"
                        >
                          {cat} ×
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Sub-Category Picker */}
                  <div className="flex flex-col gap-2">
                    <label className="text-xs text-slate-400 font-medium px-1">Sub-Categories</label>
                    {!isAddingNewSubCategory && blogSubCategories.length > 0 ? (
                      <select
                        value=""
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "__new__") {
                            setIsAddingNewSubCategory(true);
                            return;
                          }
                          handleAddSubCategory(val);
                        }}
                        className="w-full bg-slate-950 border border-slate-800 focus:border-[#0D6FEA] rounded-xl px-3 py-2 text-slate-300 outline-none text-xs"
                      >
                        <option value="">Assign existing subcategory...</option>
                        {blogSubCategories.map((sub) => (
                          <option key={sub} value={sub}>{sub}</option>
                        ))}
                        <option value="__new__">+ Create new subcategory</option>
                      </select>
                    ) : null}

                    {(isAddingNewSubCategory || blogSubCategories.length === 0) && (
                      <div className="flex gap-2">
                        <input
                          value={subCategoryDraft}
                          onChange={(e) => setSubCategoryDraft(e.target.value)}
                          placeholder="New subcategory label"
                          className="w-full bg-slate-950 border border-slate-800 focus:border-[#0D6FEA] rounded-lg px-3 py-2 text-xs outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => handleAddSubCategory(subCategoryDraft)}
                          className="px-3 bg-blue-600 hover:bg-blue-500 rounded-lg text-xs font-bold text-white cursor-pointer"
                        >
                          Add
                        </button>
                        {blogSubCategories.length > 0 && (
                          <button
                            type="button"
                            onClick={() => {
                              setIsAddingNewSubCategory(false);
                              setSubCategoryDraft("");
                            }}
                            className="px-2 bg-slate-800 rounded-lg text-xs"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    )}

                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {blogForm.sub_category.map((sub) => (
                        <button
                          key={sub}
                          type="button"
                          onClick={() => handleRemoveSubCategory(sub)}
                          className="text-[10px] px-2.5 py-1 rounded-full border border-slate-800 bg-slate-900 text-slate-300"
                        >
                          {sub} ×
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="text-xs text-slate-400 font-medium px-1">Author</label>
                    <input
                      name="author"
                      value={blogForm.author}
                      onChange={handleBlogChange}
                      placeholder="Author name"
                      className="w-full bg-slate-950 border border-slate-800 focus:border-[#0D6FEA] rounded-xl px-4 py-2.5 text-slate-100 outline-none text-xs"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs text-slate-400 font-medium px-1">Blog Cover Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleBlogFile}
                    className="w-full text-xs text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-500 cursor-pointer"
                  />
                  {editingBlogId && (
                    <p className="text-[10px] text-slate-500">Leave empty to preserve current cover image.</p>
                  )}
                  {blogForm.cover_image_url && (
                    <div className="border border-slate-800 rounded-xl p-3.5 bg-slate-950/60 max-w-sm mt-2">
                      <img
                        src={blogForm.cover_image_url}
                        alt="Current blog cover"
                        className="w-full h-auto rounded-lg object-cover border border-slate-900"
                        onError={() => setBlogImagePreviewError(true)}
                      />
                      {blogImagePreviewError && (
                        <p className="text-[10px] text-red-400 mt-2">Could not load preview image.</p>
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-2 pt-2">
                  <label className="text-xs text-slate-400 font-medium px-1">Article Body Content</label>
                  <CommentEditor
                    showSubmitButton={false}
                    withTopSpacing={false}
                    initialContent={editorInitialValue}
                    onChange={(content) => setBlogForm((prev) => ({ ...prev, content }))}
                  />
                </div>

                {blogStatus.message && (
                  <p className={`text-sm ${blogStatus.type === "success" ? "text-green-400" : "text-red-400"}`}>
                    {blogStatus.message}
                  </p>
                )}

                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={isSubmittingBlog}
                    className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-xs font-bold text-white disabled:opacity-60 cursor-pointer"
                  >
                    {isSubmittingBlog ? "Saving..." : editingBlogId ? "Update Article" : "Publish Article"}
                  </button>
                  {editingBlogId && (
                    <button
                      type="button"
                      onClick={resetBlogForm}
                      className="px-5 py-2.5 rounded-xl border border-slate-700 text-xs font-bold text-slate-400 hover:text-slate-300 cursor-pointer"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>

            {/* Blogs sidebar list */}
            <div className="xl:col-span-4 bg-slate-900/60 border border-slate-800/60 rounded-2xl p-6 shadow-md">
              <h2 className="text-lg font-bold text-white mb-4">Published Articles</h2>
              <div className="space-y-3 max-h-[750px] overflow-y-auto pr-1">
                {blogs.length === 0 ? (
                  <p className="text-sm text-slate-500">No blog posts found.</p>
                ) : (
                  blogs.map((blog) => {
                    const id = getEntityId(blog);
                    return (
                      <div
                        key={id}
                        onClick={() => onOpenBlogDetails(id)}
                        className="border border-slate-800 bg-slate-950 p-4 rounded-xl flex flex-col justify-between gap-3 cursor-pointer hover:border-slate-700 transition-all"
                      >
                        <div>
                          <p className="font-bold text-slate-200 text-sm leading-snug">{blog?.title || "Untitled"}</p>
                          <p className="text-[10px] text-slate-500 mt-1.5 truncate">
                            {[
                              ...(Array.isArray(blog?.category) ? blog.category : toStringArray(blog?.category)),
                              ...(Array.isArray(blog?.sub_category || blog?.subCategory) ? blog?.sub_category || blog?.subCategory : toStringArray(blog?.sub_category || blog?.subCategory)),
                            ]
                              .filter(Boolean)
                              .join(" / ") || "Uncategorized"}
                          </p>
                        </div>
                        <div className="flex gap-2 border-t border-slate-805 pt-2.5">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditBlog(blog);
                            }}
                            className="flex-1 px-3 py-1.5 text-xs font-bold rounded-lg bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 cursor-pointer text-center"
                          >
                            Edit
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteBlog(id);
                            }}
                            className="px-3 py-1.5 text-xs font-bold rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 cursor-pointer"
                          >
                            <Trash size={15} />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
